/**
 * `<cusa-quick-login>`
 *
 * Reusable inline customer login component intended to be embedded inside a `<cusa-dialog>`.
 * Submits legacy customer credentials via an AJAX POST to `/account/login` and surfaces errors inline
 * (without navigating away).
 *
 * IMPORTANT — Legacy customer accounts only:
 * This AJAX `/account/login` flow works only when the shop is using legacy (email + password) customer accounts.
 * If the shop migrates to Shopify Customer accounts (new), sign-in becomes passwordless (one-time code) and
 * the login flow must be rewritten to use Shopify's Customer Account API (GraphQL) / hosted customer account
 * authentication flow instead of posting to `/account/login`.
 *
 * Includes close-lock integration with `<cusa-dialog>` so the dialog stays open during submission.
 */

const MESSAGES = {
	MISSING_FIELDS: 'Please enter both email and password.',
	SECURITY_TRY_AGAIN: 'Security check required. Please try again.',
	SECURITY_REDIRECT: 'Security check required. Redirecting to the login page.',
	SECURITY_RETRY_AFTER_CHALLENGE: 'Security check complete. Please try signing in again.',
	BAD_CREDENTIALS: 'Email or password is incorrect.',
	SIGNING_IN: 'Signing you in…',
	GENERIC_FAILURE: 'Sign in failed. Please try again.',
	VERIFYING: 'Verifying security…',
	NO_INTERNET: 'No internet connection. Please check and try again.'
};

const EMAIL_SELECTOR = 'input[name="customer[email]"], input[type="email"], input[name="email"]';
const PASSWORD_SELECTOR = 'input[name="customer[password]"], input[type="password"], input[name="password"]';

/**
 * Captures a snapshot of all named, non-hidden, non-captcha form fields.
 * Always includes `email` and `password` shortcut keys for backward compatibility.
 * @param {HTMLFormElement|null} form
 * @returns {{email: string, password: string, [key: string]: string}}
 */
function snapshotFormFields(form) {
	let state = { email: '', password: '' };
	if (!(form instanceof HTMLFormElement)) return state;
	for (let el of form.elements) {
		if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement))
			continue;
		if (!el.name || el.type === 'hidden') continue;
		if (el.name === 'h-captcha-response' || el.name.includes('captcha')) continue;
		state[el.name] = el.value || '';
	}
	let emailField = form.querySelector(EMAIL_SELECTOR);
	let passwordField = form.querySelector(PASSWORD_SELECTOR);
	if (emailField instanceof HTMLInputElement) state.email = emailField.value || '';
	if (passwordField instanceof HTMLInputElement) state.password = passwordField.value || '';
	return state;
}

/**
 * Determines whether quick-login debug logging/helpers should be enabled.
 * @returns {boolean}
 */
const isQuickLoginDebugEnabled = () => {
	try {
		let host = window?.location?.hostname || '';
		return host.includes('test.') || host.includes('myshopify') || host === 'localhost' || host === '127.0.0.1';
	} catch (_) {
		return false;
	}
};

/**
 * Registers global console helpers for local/dev troubleshooting.
 */
const registerQuickLoginConsoleHelpers = () => {
	if (typeof window === 'undefined') return;
	if (typeof window.cusaQuickLoginDebugLogout === 'function') return;

	/**
	 * Logs out the current customer and reloads the page.
	 * Exposed globally for manual debugging in DevTools.
	 * @returns {Promise<void>}
	 */
	window.cusaQuickLoginDebugLogout = async () => {
		try {
			await fetch('/account/logout', {
				method: 'GET',
				credentials: 'same-origin',
				redirect: 'follow'
			});
		} catch (_) {
			// Ignore network failures and still force a reload to refresh session state.
		}
		window.location.reload();
	};
};

registerQuickLoginConsoleHelpers();

/**
 * Manages dialog open/close/locking behavior.
 */
class DialogController {
	/**
	 * @param {Element|null} element - The dialog wrapper element
	 */
	constructor(element = null) {
		this.element = element;
	}

	/**
	 * Applies a close-lock to prevent accidental closure during submission.
	 */
	lock() {
		if (!this.element) return;
		this.element.setAttribute('data-cusa-lock-close', 'true');
		this.element.querySelector('dialog')?.setAttribute('data-cusa-lock-close', 'true');
	}

	/**
	 * Removes the close-lock.
	 */
	unlock() {
		if (!this.element) return;
		this.element.removeAttribute('data-cusa-lock-close');
		this.element.querySelector('dialog')?.removeAttribute('data-cusa-lock-close');
	}

	/**
	 * Returns the native dialog element.
	 * @returns {HTMLDialogElement|null}
	 */
	getInnerDialog() {
		if (!this.element) return null;
		let dialog = this.element.querySelector('dialog');
		return dialog instanceof HTMLDialogElement ? dialog : null;
	}

	/**
	 * Closes the dialog via custom API or native dialog.close().
	 */
	close() {
		if (!this.element) return;
		if (typeof this.element.close === 'function') {
			this.element.close();
			return;
		}
		let dialog = this.getInnerDialog();
		if (dialog?.open) dialog.close();
	}

	/**
	 * Opens the dialog via custom API or native dialog.showModal().
	 */
	open() {
		if (!this.element) return;
		if (typeof this.element.open === 'function') {
			this.element.open();
			return;
		}
		let dialog = this.getInnerDialog();
		if (dialog && !dialog.open) dialog.showModal();
	}

	/**
	 * Forces cleanup of scroll locks that may have been left by Shopify's challenge UI.
	 */
	forceReleaseScrollLock() {
		try {
			let dialog = this.getInnerDialog();
			if (dialog?.open) dialog.close();
		} catch (_) {}

		document.documentElement.style.removeProperty('overflow');
		document.body.style.removeProperty('overflow');
		document.body.style.removeProperty('position');
		document.body.style.removeProperty('top');

		document.documentElement.classList.remove('cusa-dialog-scroll-locked');
		document.body.classList.remove('cusa-dialog-scroll-locked');

		if (this.element) {
			this.element.removeAttribute('data-cusa-lock-scroll');
			this.element.querySelector('dialog')?.removeAttribute('data-cusa-lock-scroll');
		}
	}
}

/**
 * Manages accessibility state for form fields and messages.
 */
class AccessibilityManager {
	/**
	 * @param {Element} root - The login form container
	 */
	constructor(root) {
		this.root = root;
	}

	/**
	 * Ensures the message region has a stable id for aria-describedby references.
	 * @returns {string}
	 */
	ensureMessageRegionId() {
		let messageEl = this.root.querySelector('[data-cusa-login-message]');
		if (!messageEl) return '';
		let id = messageEl.getAttribute('id');
		if (id) return id;
		id = `cusa-quick-login-message-${Math.random().toString(36).slice(2, 9)}`;
		messageEl.setAttribute('id', id);
		return id;
	}

	/**
	 * Clears aria-invalid and aria-describedby from fields.
	 * @param {HTMLFormElement} form
	 */
	clearFieldState(form) {
		if (!form) return;
		let messageId = this.ensureMessageRegionId();
		let fields = [form.querySelector(EMAIL_SELECTOR), form.querySelector(PASSWORD_SELECTOR)].filter(
			(f) => f instanceof HTMLInputElement
		);

		for (let field of fields) {
			field.removeAttribute('aria-invalid');
			if (messageId) {
				let parts = (field.getAttribute('aria-describedby') || '')
					.trim()
					.split(/\s+/)
					.filter((id) => id !== messageId);
				if (parts.length) field.setAttribute('aria-describedby', parts.join(' '));
				else field.removeAttribute('aria-describedby');
			}
		}
	}

	/**
	 * Marks specific fields as invalid and associates with message region.
	 * @param {HTMLFormElement} form
	 * @param {{ email?: boolean, password?: boolean }} invalid
	 */
	setFieldInvalid(form, invalid = {}) {
		if (!form) return;
		let messageId = this.ensureMessageRegionId();
		let emailField = form.querySelector(EMAIL_SELECTOR);
		let passwordField = form.querySelector(PASSWORD_SELECTOR);

		for (let [field, isInvalid] of [
			[emailField, invalid.email],
			[passwordField, invalid.password]
		]) {
			if (!(field instanceof HTMLInputElement)) continue;
			if (isInvalid) field.setAttribute('aria-invalid', 'true');
			else field.removeAttribute('aria-invalid');

			if (messageId) {
				let parts = (field.getAttribute('aria-describedby') || '').trim().split(/\s+/).filter(Boolean);
				if (!parts.includes(messageId)) parts.push(messageId);
				field.setAttribute('aria-describedby', parts.join(' '));
			}
		}
	}

	/**
	 * Focuses an element without scrolling.
	 * @param {HTMLElement|null} el
	 */
	focus(el) {
		if (!el) return;
		try {
			el.focus({ preventScroll: true });
		} catch {
			el.focus();
		}
	}

	/**
	 * Focuses the message region for screen reader announcement.
	 */
	focusMessage() {
		let messageEl = this.root.querySelector('[data-cusa-login-message]');
		if (!(messageEl instanceof HTMLElement)) return;
		messageEl.setAttribute('tabindex', '-1');
		requestAnimationFrame(() => this.focus(messageEl));
	}
}

/**
 * Manages hCaptcha token retrieval and retry logic.
 */
class CaptchaManager {
	/**
	 * Initializes captcha execution and retry state.
	 */
	constructor() {
		this.lastExecuteAt = 0;
		this.cooldownUntil = 0;
		this.fallbackState = new WeakMap();
		this.softAttempts = 3;
		this.resetWindowMs = 3 * 60 * 1000;
	}

	/**
	 * Checks if the form is protected by captcha.
	 * @param {HTMLFormElement} form
	 * @returns {boolean}
	 */
	isProtected(form) {
		return (
			form?.hasAttribute('data-hcaptcha-bound') ||
			form?.hasAttribute('data-cptcha') ||
			form?.hasAttribute('data-sitekey') ||
			form?.querySelector('.h-captcha') ||
			form?.querySelector('textarea[name="h-captcha-response"]') ||
			form?.querySelector('[data-hcaptcha-widget-id]')
		);
	}

	/**
	 * Gets the current hCaptcha token.
	 * Resolves in fallback order: textarea value → hcaptcha.getResponse(widgetId) → iframe data attribute.
	 * @param {HTMLFormElement} form
	 * @returns {string}
	 */
	getToken(form) {
		let token = form?.querySelector('textarea[name="h-captcha-response"]')?.value?.trim() || '';
		if (token) return token;

		try {
			let widgetId = this.getWidgetId(form);
			if (widgetId && typeof window !== 'undefined' && typeof window.hcaptcha?.getResponse === 'function') {
				token = window.hcaptcha.getResponse(widgetId)?.trim() || '';
				if (token) return token;
			}
		} catch (_) {}

		try {
			let iframe = form?.querySelector('iframe[data-hcaptcha-widget-id]');
			token = iframe?.getAttribute('data-hcaptcha-response')?.trim() || '';
			if (token) return token;
		} catch (_) {}

		return '';
	}

	/**
	 * Gets the hCaptcha widget ID from injected iframe.
	 * @param {HTMLFormElement} form
	 * @returns {string}
	 */
	getWidgetId(form) {
		let iframe = form?.querySelector('iframe[data-hcaptcha-widget-id]');
		return iframe?.getAttribute('data-hcaptcha-widget-id')?.trim() || '';
	}

	/**
	 * Ensures a token exists, trying to execute captcha if needed.
	 * Falls back to rendering a visible widget if invisible fails.
	 * @param {HTMLFormElement} form
	 * @param {number} timeoutMs
	 * @param {AbortSignal} signal
	 * @returns {Promise<boolean>}
	 */
	async ensureToken(form, timeoutMs = 4000, signal) {
		if (!this.isProtected(form)) return true;
		if (this.getToken(form)) return true;

		let now = Date.now();
		if (this.cooldownUntil && now < this.cooldownUntil) return false;

		try {
			let widgetId = this.getWidgetId(form);
			if (widgetId && typeof window !== 'undefined' && window.hcaptcha?.execute) {
				let sinceLast = now - this.lastExecuteAt;
				if (sinceLast > 1500) {
					this.lastExecuteAt = now;
					try {
						window.hcaptcha.execute(widgetId, { async: true });
					} catch (_) {
						window.hcaptcha.execute(widgetId);
					}
				}
			}
		} catch (_) {}

		let start = Date.now();
		let lateRetryDone = false;
		while (Date.now() - start < timeoutMs) {
			if (signal?.aborted) return false;
			if (this.getToken(form)) return true;

			let elapsed = Date.now() - start;
			if (!lateRetryDone && elapsed >= timeoutMs * 0.75) {
				lateRetryDone = true;
				try {
					let widgetId = this.getWidgetId(form);
					if (widgetId && typeof window !== 'undefined' && window.hcaptcha?.execute) {
						try {
							window.hcaptcha.reset(widgetId);
						} catch (_) {}
						try {
							window.hcaptcha.execute(widgetId, { async: true });
						} catch (_) {
							window.hcaptcha.execute(widgetId);
						}
						this.lastExecuteAt = Date.now();
					}
				} catch (_) {}
			}

			await new Promise((r) => setTimeout(r, 100));
		}

		return Boolean(this.getToken(form));
	}

	/**
	 * Checks if we should hard-handoff to native challenge flow.
	 * @param {HTMLFormElement} form
	 * @returns {boolean}
	 */
	shouldHandoff(form) {
		let now = Date.now();
		let state = this.fallbackState.get(form);

		if (!state?.firstAt || now - state.firstAt > this.resetWindowMs) {
			state = { count: 0, firstAt: now };
		}

		state.count += 1;
		this.fallbackState.set(form, state);
		return state.count > this.softAttempts;
	}

	/**
	 * Sets a cooldown period to prevent rapid captcha executions.
	 * @param {number} ms - Milliseconds to cooldown
	 */
	setCooldown(ms) {
		this.cooldownUntil = Date.now() + ms;
	}

	/**
	 * Checks whether an hCaptcha visual challenge overlay is currently injected in the DOM.
	 * This detects the interactive challenge popup, not the invisible bound widget.
	 * @returns {boolean}
	 */
	isChallengeVisible() {
		let selectors = [
			'iframe[src*="hcaptcha.com/captcha"]',
			'iframe[title*="hCaptcha challenge"]:not([data-hcaptcha-widget-id])'
		];
		for (let s of selectors) {
			try {
				if (document.querySelector(s)) return true;
			} catch (_) {}
		}
		return false;
	}
}

/**
 * Manages cross-navigation session state for reopening dialogs after challenges.
 */
class SessionMemory {
	static STATE_KEY = 'cusaQuickLoginState';
	static LEGACY_KEYS = ['cusaQuickLoginReopen', 'cusaQuickLoginLastHandoff', 'cusaQuickLoginFlowSummary'];
	static MAX_FLOW_EVENTS = 40;
	static MAX_STORED_FLOWS = 3;

	/**
	 * Creates an empty quick-login flow state.
	 * @param {string|null} [flowId]
	 * @returns {{flowId:string|null,reopen:Object|null,lastHandoff:Object|null,flowSummary:Array<Object>,attemptCounter:number}}
	 */
	static createEmptyState(flowId = null) {
		return {
			flowId: typeof flowId === 'string' && flowId ? flowId : null,
			reopen: null,
			lastHandoff: null,
			flowSummary: [],
			attemptCounter: 0
		};
	}

	/**
	 * Generates a unique identifier for a persisted quick-login flow.
	 * @returns {string}
	 */
	static createFlowId() {
		return `cql_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
	}

	/**
	 * Normalizes a stored flow state into the current canonical shape.
	 * @param {Object|null} state
	 * @param {string|null} [fallbackFlowId]
	 * @returns {{flowId:string|null,reopen:Object|null,lastHandoff:Object|null,flowSummary:Array<Object>,attemptCounter:number}}
	 */
	static normalizeState(state, fallbackFlowId = null) {
		if (!state || typeof state !== 'object') return this.createEmptyState(fallbackFlowId);
		let flowSummary = Array.isArray(state.flowSummary) ? state.flowSummary : [];
		let maxAttemptFromSummary = this.getMaxAttemptId(flowSummary);
		let parsedAttemptCounter = Number.isFinite(state.attemptCounter) ? Number(state.attemptCounter) : 0;
		let flowId = typeof state.flowId === 'string' && state.flowId ? state.flowId : fallbackFlowId;
		return {
			flowId: typeof flowId === 'string' && flowId ? flowId : null,
			reopen: state.reopen || null,
			lastHandoff: state.lastHandoff || null,
			flowSummary,
			attemptCounter: Math.max(parsedAttemptCounter, maxAttemptFromSummary)
		};
	}

	/**
	 * Returns the most recent timestamp associated with a flow state.
	 * @param {{reopen:Object|null,lastHandoff:Object|null,flowSummary:Array<Object>}} state
	 * @returns {number}
	 */
	static getStateTimestamp(state) {
		let flowSummary = Array.isArray(state?.flowSummary) ? state.flowSummary : [];
		let lastEventTime = flowSummary.reduce((max, event) => {
			let timestamp = Number(event?.timestamp);
			return Number.isFinite(timestamp) ? Math.max(max, timestamp) : max;
		}, 0);
		let reopenTime = Number(state?.reopen?.timestamp);
		let handoffTime = Number(state?.lastHandoff?.timestamp);
		return Math.max(
			lastEventTime,
			Number.isFinite(reopenTime) ? reopenTime : 0,
			Number.isFinite(handoffTime) ? handoffTime : 0
		);
	}

	/**
	 * Reads the full persisted flow envelope from session storage.
	 * @returns {{activeFlowId:string|null,flows:Object<string, Object>}}
	 */
	static loadEnvelope() {
		if (typeof window === 'undefined') return { activeFlowId: null, flows: {} };
		try {
			let raw = getSessionStorage(this.STATE_KEY);
			if (!raw) return { activeFlowId: null, flows: {} };
			let parsed = JSON.parse(raw);

			if (parsed && typeof parsed === 'object' && parsed.flows && typeof parsed.flows === 'object') {
				let flows = {};
				for (let [flowId, state] of Object.entries(parsed.flows)) {
					if (typeof flowId !== 'string' || !flowId) continue;
					flows[flowId] = this.normalizeState(state, flowId);
				}
				let activeFlowId =
					typeof parsed.activeFlowId === 'string' && parsed.activeFlowId && flows[parsed.activeFlowId]
						? parsed.activeFlowId
						: null;
				return { activeFlowId, flows };
			}

			if (parsed && typeof parsed === 'object') {
				let flowId = typeof parsed.flowId === 'string' && parsed.flowId ? parsed.flowId : this.createFlowId();
				return {
					activeFlowId: flowId,
					flows: {
						[flowId]: this.normalizeState(parsed, flowId)
					}
				};
			}
		} catch (_) {
			return { activeFlowId: null, flows: {} };
		}
		return { activeFlowId: null, flows: {} };
	}

	/**
	 * Returns flow ids ordered from newest to oldest.
	 * @param {{flows:Object<string, Object>}} envelope
	 * @returns {Array<string>}
	 */
	static getSortedFlowIds(envelope) {
		return Object.keys(envelope?.flows || {}).sort(
			(a, b) => this.getStateTimestamp(envelope.flows[b]) - this.getStateTimestamp(envelope.flows[a])
		);
	}

	/**
	 * Returns the currently active persisted flow id.
	 * @param {{activeFlowId:string|null,flows:Object<string, Object>}|null} [envelope]
	 * @returns {string|null}
	 */
	static getActiveFlowId(envelope = null) {
		let source = envelope || this.loadEnvelope();
		if (source.activeFlowId && source.flows[source.activeFlowId]) return source.activeFlowId;
		return this.getSortedFlowIds(source)[0] || null;
	}

	/**
	 * Returns the highest numeric attempt id in a flow summary.
	 * @param {Array<Object>} flowSummary
	 * @returns {number}
	 */
	static getMaxAttemptId(flowSummary) {
		if (!Array.isArray(flowSummary)) return 0;
		let max = 0;
		for (let event of flowSummary) {
			let id = Number(event?.attemptId);
			if (Number.isFinite(id) && id > max) max = id;
		}
		return max;
	}

	/**
	 * Removes superseded session storage keys from prior implementations.
	 */
	static purgeLegacyKeys() {
		if (typeof window === 'undefined') return [];
		try {
			for (let key of this.LEGACY_KEYS) {
				sessionStorage.removeItem(key);
			}
		} catch (_) {
			// no-op
		}
	}

	/**
	 * Completely resets the stored quick-login state. This is intended to be
	 * invoked when the page is freshly loaded or explicitly reloaded by the
	 * user so that any previous attempt counters / flow summaries are cleared.
	 *
	 * We deliberately only wipe the state on hard reloads (navigation type
	 * "reload") so that legitimate challenge-reopen payloads are preserved when
	 * the user is returned from a Shopify challenge flow (those navigations are
	 * normal "navigate" entries).
	 */
	static resetOnReload() {
		if (typeof window === 'undefined') return;
		try {
			let navEntry = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || {};
			if (navEntry.type === 'reload') {
				// drop everything, legacy keys will also be purged by saveState
				sessionStorage.removeItem(this.STATE_KEY);
				this.purgeLegacyKeys();
			}
		} catch (_) {
			// ignore; defensive because performance API may not exist in some contexts
		}
	}

	/**
	 * Returns true if the current document contains **any** quick-login trigger
	 * element.  Earlier versions only cleared session state on reload when a
	 * *visible* trigger was detected; that visibility check proved brittle when
	 * the trigger was hidden by CSS or off‑screen, leading to stale state
	 * lingering.  Presence of the trigger is sufficient, since wiping unused
	 * state is harmless and more reliable.
	 *
	 * @returns {boolean}
	 */
	static pageHasQuickLoginTrigger() {
		if (typeof document === 'undefined') return false;
		return document.querySelector('[data-dialog-trigger="quick-login"]') !== null;
	}

	/**
	 * Reads canonical quick-login state from sessionStorage.
	 * @param {string|null} [flowId]
	 * @returns {{flowId:string|null,reopen:Object|null,lastHandoff:Object|null,flowSummary:Array<Object>,attemptCounter:number}}
	 */
	static loadState(flowId = null) {
		if (typeof window === 'undefined') return this.createEmptyState(flowId);
		let envelope = this.loadEnvelope();
		let resolvedFlowId = flowId || this.getActiveFlowId(envelope);
		if (resolvedFlowId && envelope.flows[resolvedFlowId]) {
			return this.normalizeState(envelope.flows[resolvedFlowId], resolvedFlowId);
		}
		return this.createEmptyState(flowId);
	}

	/**
	 * Persists canonical quick-login state to sessionStorage.
	 * @param {{flowId:string|null,reopen:Object|null,lastHandoff:Object|null,flowSummary:Array<Object>,attemptCounter:number}} state
	 * @returns {{flowId:string|null,reopen:Object|null,lastHandoff:Object|null,flowSummary:Array<Object>,attemptCounter:number}|null}
	 */
	static saveState(state) {
		if (typeof window === 'undefined') return;
		try {
			this.purgeLegacyKeys();
			let envelope = this.loadEnvelope();
			let copy = { ...state };
			if (copy.hasOwnProperty('version')) {
				delete copy.version;
			}
			let flowId = typeof copy.flowId === 'string' && copy.flowId ? copy.flowId : this.createFlowId();
			let normalized = this.normalizeState(copy, flowId);

			// Keep bounded flow history so aborted/double-submit attempts do not accumulate indefinitely.
			let maxStoredFlows = Number(this.MAX_STORED_FLOWS);
			if (Number.isFinite(maxStoredFlows) && maxStoredFlows > 0) {
				let existingFlowIds = this.getSortedFlowIds(envelope);
				let keepExistingCount = Math.max(maxStoredFlows - 1, 0);
				for (let staleFlowId of existingFlowIds.slice(keepExistingCount)) {
					delete envelope.flows[staleFlowId];
				}
			}

			envelope.flows[flowId] = normalized;
			envelope.activeFlowId = flowId;
			setSessionStorage(this.STATE_KEY, JSON.stringify(envelope));
			return normalized;
		} catch (_) {
			return null;
		}
	}

	/**
	 * Starts a fresh persisted flow entry for a new login attempt.
	 * @param {number} [attemptCounter]
	 * @returns {{flowId:string|null,reopen:Object|null,lastHandoff:Object|null,flowSummary:Array<Object>,attemptCounter:number}}
	 */
	static startNewFlow(attemptCounter = 0) {
		let state = this.createEmptyState(this.createFlowId());
		if (Number.isFinite(attemptCounter)) {
			state.attemptCounter = Math.max(0, Number(attemptCounter));
		}
		return this.saveState(state) || state;
	}

	/**
	 * Returns the stored flow summary timeline.
	 * @param {string|null} [flowId]
	 * @returns {Array<Object>}
	 */
	static getFlowSummary(flowId = null) {
		let state = this.loadState(flowId);
		return Array.isArray(state?.flowSummary) ? state.flowSummary : [];
	}

	/**
	 * Returns the highest monotonic attempt counter across stored flows.
	 * @returns {number}
	 */
	static getAttemptCounter() {
		let envelope = this.loadEnvelope();
		let maxAttemptCounter = 0;
		for (let state of Object.values(envelope.flows)) {
			let normalized = this.normalizeState(state);
			maxAttemptCounter = Math.max(maxAttemptCounter, Number(normalized.attemptCounter) || 0);
		}
		return maxAttemptCounter;
	}

	/**
	 * Appends an event to the flow summary timeline.
	 * @param {string} event
	 * @param {Object} [meta]
	 * @param {string|null} [flowId]
	 */
	static appendFlowSummary(event, meta = {}, flowId = null) {
		if (typeof window === 'undefined') return;
		try {
			let state = this.loadState(flowId);
			if (!state.flowId) state.flowId = flowId || this.createFlowId();
			let events = Array.isArray(state.flowSummary) ? state.flowSummary : [];
			events.push({
				timestamp: Date.now(),
				path: `${window.location.pathname}${window.location.search}`,
				event: event || '',
				...meta
			});
			if (events.length > this.MAX_FLOW_EVENTS) events = events.slice(-this.MAX_FLOW_EVENTS);
			state.flowSummary = events;
			let eventAttemptId = Number(meta?.attemptId);
			if (Number.isFinite(eventAttemptId)) {
				state.attemptCounter = Math.max(Number(state.attemptCounter) || 0, eventAttemptId);
			}
			this.saveState(state);
		} catch (_) {}
	}

	/**
	 * Records the current dialog state for potential reopening after challenge.
	 * @param {Element} element - The dialog element to remember
	 * @param {HTMLFormElement|null} form - Login form to snapshot values from
	 */
	static rememberReopen(element, form = null, attemptId = null, flowId = null) {
		if (typeof window === 'undefined') return;
		try {
			if (!element) return;
			let id = element.getAttribute('id') || 'default';
			let modalKey = element.getAttribute('data-cusa-modal') || '';
			let focusField = '';
			let formState = snapshotFormFields(form instanceof HTMLFormElement ? form : null);

			let active = document.activeElement;
			if (active instanceof HTMLInputElement && element.contains(active)) {
				focusField = active.name || active.type || '';
			}

			let state = this.loadState(flowId);
			if (!state.flowId) state.flowId = flowId || this.createFlowId();
			state.reopen = {
				timestamp: Date.now(),
				flowId: state.flowId,
				id,
				modalKey,
				path: `${window.location.pathname}${window.location.search}`,
				focusField,
				formState,
				attemptId: typeof attemptId === 'number' ? attemptId : null
			};
			this.saveState(state);
		} catch (_) {}
	}

	/**
	 * Retrieves stored reopen state if valid and on the same page.
	 * @returns {Object|null}
	 */
	static retrieveReopen() {
		if (typeof window === 'undefined') return null;
		try {
			let currentPath = `${window.location.pathname}${window.location.search}`;
			let envelope = this.loadEnvelope();
			for (let flowId of this.getSortedFlowIds(envelope)) {
				let payload = this.normalizeState(envelope.flows[flowId], flowId)?.reopen;
				if (!payload?.timestamp) continue;
				if (payload.path !== currentPath) continue;

				if (Date.now() - payload.timestamp > 5 * 60 * 1000) {
					this.clearReopen(flowId);
					continue;
				}

				return {
					...payload,
					flowId
				};
			}

			return null;
		} catch (_) {
			return null;
		}
	}

	/**
	 * Clears the stored reopen state.
	 * @param {string|null} [flowId]
	 */
	static clearReopen(flowId = null) {
		if (typeof window === 'undefined') return;
		try {
			let state = this.loadState(flowId);
			if (!state.flowId) return;
			state.reopen = null;
			this.saveState(state);
		} catch (_) {}
	}

	/**
	 * Records details about a handoff to challenge flow.
	 * @param {HTMLFormElement} form
	 * @param {string} reason
	 * @param {string} destinationUrl
	 */
	static recordHandoff(form, reason, destinationUrl, flowId = null) {
		if (typeof window === 'undefined') return;
		try {
			let state = this.loadState(flowId);
			if (!state.flowId) state.flowId = flowId || this.createFlowId();
			state.lastHandoff = {
				timestamp: Date.now(),
				flowId: state.flowId,
				reason: reason || '',
				destinationURL: destinationUrl || '',
				action: form?.action || '',
				method: form?.method || 'POST',
				path: `${window.location.pathname}${window.location.search}`,
				flowSummary: this.getFlowSummary(state.flowId)
			};
			this.saveState(state);
		} catch (_) {}
	}
}

/**
 * Main login component that orchestrates form submission, dialog management, and AJAX authentication.
 */
// When the script loads we may be arriving via a hard reload, in which
// case any previous quick-login state should be discarded so the attempt
// counter starts from zero.  Previously we only wiped state if a *visible*
// quick-login trigger was present; this visibility test could fail when the
// trigger was hidden, leaving stale data behind.  We now clear state whenever
// any trigger element exists, which is safe and more dependable.
if (typeof window !== 'undefined') {
	if (SessionMemory.pageHasQuickLoginTrigger()) {
		SessionMemory.resetOnReload();
	}
}

class CusaQuickLogin extends HTMLElement {
	/**
	 * Initializes the component with bound event handlers and helper instances.
	 */
	constructor() {
		super();
		this.onSubmit = this.onSubmit.bind(this);
		this.onLoginButtonClick = this.onLoginButtonClick.bind(this);
		this.onLoginButtonPointerDown = this.onLoginButtonPointerDown.bind(this);
		this.onDialogClose = this.onDialogClose.bind(this);
		this.onPageShow = this.onPageShow.bind(this);

		// Current login attempt state
		this.loginAbortController = null;
		this.loginAttemptId = SessionMemory.getAttemptCounter();
		this.currentFlowId = SessionMemory.getActiveFlowId();
		this.activeElement = null;
		this.activeForm = null;
		this.activeRoot = null;

		// Helper instances
		this.a11y = null;
		this.captcha = new CaptchaManager();

		// Captcha close-and-reopen state
		this._captchaPendingPayload = null;
		this._captchaWatchTimer = null;
		this._challengeVisibilityTimer = null;

		// The inner <dialog> element currently listening for 'close' to abort in-flight fetches
		this._boundCloseDialog = null;
	}

	/**
	 * Logs debug events and mirrors them to session storage flow summary.
	 * @param {string} message
	 * @param {Object|null} [meta]
	 */
	logDebug(message, meta = null) {
		let attemptId = typeof meta?.attemptId === 'number' ? meta.attemptId : this.loginAttemptId;
		let prefix = `[cusa-quick-login][attempt:${attemptId}]`;
		if (meta && typeof meta === 'object') {
			let details = { ...meta };
			if (typeof details.attemptId !== 'number') details.attemptId = attemptId;
			SessionMemory.appendFlowSummary(message, details, this.currentFlowId);
			if (!isQuickLoginDebugEnabled()) return;
			console.log(`${prefix} ${message}`, details);
			return;
		}
		SessionMemory.appendFlowSummary(message, { attemptId }, this.currentFlowId);
		if (!isQuickLoginDebugEnabled()) return;
		console.log(`${prefix} ${message}`);
	}

	/**
	 * Starts a new attempt id and persists it to session state.
	 * @returns {number}
	 */
	beginAttempt() {
		this.loginAttemptId += 1;
		let flowState = SessionMemory.startNewFlow(this.loginAttemptId);
		this.currentFlowId = flowState.flowId;
		return this.loginAttemptId;
	}

	/**
	 * Binds the dialog `close` event to the given inner dialog element so that any
	 * modal dismissal aborts in-flight fetch requests.  Replaces any prior binding on
	 * a different element and removes the listener when called with null.
	 * @param {HTMLDialogElement|null} innerDialog
	 */
	_bindDialogClose(innerDialog) {
		if (innerDialog === this._boundCloseDialog) return;
		if (this._boundCloseDialog) {
			this._boundCloseDialog.removeEventListener('close', this.onDialogClose);
		}
		this._boundCloseDialog = innerDialog instanceof HTMLDialogElement ? innerDialog : null;
		if (this._boundCloseDialog) {
			this._boundCloseDialog.addEventListener('close', this.onDialogClose, { passive: true });
		}
	}

	/**
	 * Sets up event listeners and initializes accessibility manager.
	 */
	connectedCallback() {
		this.a11y = new AccessibilityManager(this);
		this.addEventListener('submit', this.onSubmit, true);
		this.addEventListener('click', this.onLoginButtonClick, true);
		this.addEventListener('pointerdown', this.onLoginButtonPointerDown, true);
		window.addEventListener('pageshow', this.onPageShow, { passive: true });
		let parentWrapper = this.closest('cusa-dialog') || this.closest('[data-cusa-modal]');
		this._bindDialogClose(parentWrapper?.querySelector('dialog') ?? null);
		this.focusChallengeUiIfPresent();
		this.tryReopenAfterChallenge();
		this.a11y.ensureMessageRegionId();
	}

	/**
	 * Cleans up event listeners and cancels any in-flight requests.
	 */
	disconnectedCallback() {
		this.removeEventListener('submit', this.onSubmit, true);
		this.removeEventListener('click', this.onLoginButtonClick, true);
		this.removeEventListener('pointerdown', this.onLoginButtonPointerDown, true);
		window.removeEventListener('pageshow', this.onPageShow);
		this._bindDialogClose(null);
		this._stopChallengeVisibilityWatch();
		this._stopCaptchaTokenWatch();
		this.cancelInFlight('component-disconnected');
	}

	/**
	 * Finds the login form container element.
	 * @param {HTMLFormElement} form
	 * @returns {Element}
	 */
	getLoginRoot(form) {
		return form.closest('[data-cusa-inline-login]') || this;
	}

	/**
	 * Finds the email input field in the form.
	 * @param {HTMLFormElement} form
	 * @returns {HTMLInputElement|null}
	 */
	getEmailField(form) {
		let el = form?.querySelector(EMAIL_SELECTOR);
		return el instanceof HTMLInputElement ? el : null;
	}

	/**
	 * Finds the password input field in the form.
	 * @param {HTMLFormElement} form
	 * @returns {HTMLInputElement|null}
	 */
	getPasswordField(form) {
		let el = form?.querySelector(PASSWORD_SELECTOR);
		return el instanceof HTMLInputElement ? el : null;
	}

	/**
	 * Focuses a given element without scrolling if supported.
	 * @param {HTMLElement|null} el
	 */
	focusEl(el) {
		if (!el) return;
		try {
			el.focus({ preventScroll: true });
		} catch {
			el.focus();
		}
	}

	/**
	 * Focuses the preferred login field in the dialog and restores caret position.
	 * @param {Element} wrapper
	 * @param {string} [preferredField]
	 */
	focusLoginField(wrapper, preferredField = '') {
		if (!wrapper) return;
		let scope = wrapper.querySelector('[data-cusa-inline-login]') || wrapper;

		let preferred =
			(preferredField ? scope.querySelector(`input[name="${preferredField}"]`) : null) ||
			scope.querySelector('input[name="customer[email]"]') ||
			scope.querySelector('input[type="email"]') ||
			scope.querySelector('input[name="email"]') ||
			scope.querySelector('input[name="customer[password]"]') ||
			scope.querySelector('input[type="password"]') ||
			scope.querySelector('input[name="password"]');

		let isFocusable = (el) => {
			if (!(el instanceof HTMLElement)) return false;
			if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return false;
			try {
				let style = window.getComputedStyle(el);
				return !(style.display === 'none' || style.visibility === 'hidden' || el.getClientRects().length === 0);
			} catch {
				return true;
			}
		};

		let target = isFocusable(preferred) ? preferred : null;
		if (!target) {
			target = scope.querySelector(
				'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
			);
			if (!isFocusable(target)) target = null;
		}

		if (!target) return;

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				try {
					target.focus({ preventScroll: true });
					if (target instanceof HTMLInputElement) {
						let len = target.value.length;
						target.setSelectionRange(len, len);
					}
				} catch (_) {}
			});
		});
	}

	/**
	 * Restores saved form field values after challenge navigation.
	 * Handles extended formState with all named fields as well as the email/password shortcuts.
	 * @param {Element} element
	 * @param {{formState?: {email?: string, password?: string, [key: string]: string}}} payload
	 */
	restoreFormState(element, payload) {
		if (!element || !payload?.formState) return;
		let form = element.querySelector('form.cusa-quick-login-form');
		if (!(form instanceof HTMLFormElement)) return;

		// Restore all named fields from extended formState (skip shortcut keys handled below).
		for (let [name, value] of Object.entries(payload.formState)) {
			if (name === 'email' || name === 'password') continue;
			if (typeof value !== 'string') continue;
			try {
				let el = form.querySelector(`[name="${CSS.escape(name)}"]`);
				if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
					el.value = value;
				}
			} catch (_) {}
		}

		let emailField = this.getEmailField(form);
		let passwordField = this.getPasswordField(form);
		if (emailField && typeof payload.formState.email === 'string') emailField.value = payload.formState.email;
		if (passwordField && typeof payload.formState.password === 'string')
			passwordField.value = payload.formState.password;
		this.logDebug('Restored form state after challenge', {
			hasEmail: Boolean(payload.formState.email),
			hasPassword: Boolean(payload.formState.password)
		});
	}

	/**
	 * Hides rendered Shopify challenge markup after returning to quick-login modal.
	 */
	hideChallengeUiIfPresent() {
		let challengeContainer = document.querySelector('.shopify-challenge__container');
		if (!(challengeContainer instanceof HTMLElement)) return;
		let root = challengeContainer.closest('.shopify-section') || challengeContainer;
		if (!(root instanceof HTMLElement)) return;
		root.setAttribute('hidden', 'hidden');
		root.setAttribute('aria-hidden', 'true');
	}

	/**
	 * Focuses interactive challenge UI when challenge page is active.
	 */
	focusChallengeUiIfPresent() {
		let challengeRoot =
			document.querySelector('.shopify-challenge__container') ||
			document.querySelector('form[action*="/challenge"]') ||
			document.querySelector('[class*="shopify-challenge"]');
		if (!(challengeRoot instanceof HTMLElement)) return;
		this.logDebug('Detected challenge UI; moving focus to challenge');

		let focusTarget =
			challengeRoot.querySelector('input:not([type="hidden"]), button, textarea, select, iframe, [tabindex]') ||
			challengeRoot;
		if (!(focusTarget instanceof HTMLElement)) return;
		if (focusTarget === challengeRoot && !focusTarget.hasAttribute('tabindex')) {
			focusTarget.setAttribute('tabindex', '-1');
		}

		requestAnimationFrame(() => this.focusEl(focusTarget));
	}

	/**
	 * Attempts to reopen a dialog if a reopen payload was stored after a challenge.
	 * Restores focus and releases scroll lock as needed.
	 */
	tryReopenAfterChallenge() {
		let payload = SessionMemory.retrieveReopen();
		if (!payload) return;
		this.currentFlowId = payload.flowId || this.currentFlowId;
		this.logDebug('Found reopen payload', {
			attemptId: payload.attemptId,
			dialogId: payload.id || '',
			modalKey: payload.modalKey || '',
			focusField: payload.focusField || ''
		});

		let element = null;
		if (payload.id) element = document.getElementById(payload.id);
		if (!element && payload.modalKey) element = document.querySelector(`[data-cusa-modal="${payload.modalKey}"]`);
		if (!element) element = this.activeElement || this.closest('cusa-dialog');

		if (element) {
			let dialog = new DialogController(element);
			dialog.forceReleaseScrollLock();
			dialog.open();
			dialog.unlock();
			this.hideChallengeUiIfPresent();
			this.restoreFormState(element, payload);
			let root = element.querySelector('[data-cusa-inline-login]') || this;
			this.setBusy(root, false);
			this.setSubmitLoading(root, false);
			this.setMessage(root, MESSAGES.SECURITY_RETRY_AFTER_CHALLENGE, 'info');
			this.focusLoginField(element, payload.focusField || '');
			this.a11y.focusMessage();
			SessionMemory.clearReopen(payload.flowId);
			this.logDebug('Reopened quick login after challenge');
		}
	}

	/**
	 * Handler for the `pageshow` event to reattempt reopening dialog after navigation.
	 * @param {Event} _e
	 */
	onPageShow(_e) {
		this.focusChallengeUiIfPresent();
		this.tryReopenAfterChallenge();
	}

	/**
	 * Toggles busy/disabled attributes on the root and submit button.
	 * @param {Element} root
	 * @param {boolean} busy
	 */
	setBusy(root, busy) {
		root.setAttribute('aria-busy', busy ? 'true' : 'false');
		let btn = root.querySelector('[data-cusa-login-submit]');
		if (btn) {
			btn.disabled = !!busy;
			btn.setAttribute('aria-disabled', busy ? 'true' : 'false');
		}
	}

	/**
	 * Shows or hides the loading spinner inside the submit button.
	 * @param {Element} root
	 * @param {boolean} loading
	 */
	setSubmitLoading(root, loading) {
		let btn = root.querySelector('[data-cusa-login-submit]');
		if (!btn) return;
		let spinner = btn.querySelector('.loading__spinner');
		let text = btn.querySelector('[data-cusa-login-submit-text]');
		let loadingText = btn.querySelector('[data-cusa-login-loading-text]');

		btn.classList.toggle('is-loading', !!loading);
		spinner?.classList.toggle('hidden', !loading);
		text?.classList.toggle('is-hidden', !!loading);

		if (spinner) spinner.setAttribute('aria-hidden', loading ? 'false' : 'true');
		if (text) text.setAttribute('aria-hidden', loading ? 'true' : 'false');
		if (loadingText) loadingText.setAttribute('aria-hidden', loading ? 'false' : 'true');
	}

	/**
	 * Updates the message element with given text and accessibility attributes.
	 * @param {Element} root
	 * @param {string} text
	 * @param {'info'|'error'} [type]
	 */
	setMessage(root, text, type = 'info') {
		let el = root.querySelector('[data-cusa-login-message]');
		if (!el) return;
		el.textContent = text || '';
		el.hidden = !text;
		el.setAttribute('role', type === 'error' ? 'alert' : 'status');
		el.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
		el.setAttribute('aria-atomic', 'true');
		el.setAttribute('data-state', type);
	}

	/**
	 * Clears any displayed message.
	 * @param {Element} root
	 */
	clearMessage(root) {
		this.setMessage(root, '');
	}

	/**
	 * Aborts any ongoing login attempt and cleans up UI state.
	 * @param {string} reason - for diagnostic purposes
	 */
	cancelInFlight(reason) {
		this._stopChallengeVisibilityWatch();
		this._stopCaptchaTokenWatch();
		this._captchaPendingPayload = null;
		this.loginAbortController?.abort?.();
		this.loginAbortController = null;

		if (this.activeRoot && this.activeElement) {
			let dialog = new DialogController(this.activeElement);
			this.resetDialogState(this.activeRoot, dialog);
		}

		this.activeElement = null;
		this.activeForm = null;
		this.activeRoot = null;
	}

	/**
	 * Starts a polling loop that reopens the modal once a captcha token appears.
	 * @param {HTMLFormElement} form
	 */
	_startCaptchaTokenWatch(form) {
		this._stopCaptchaTokenWatch();
		let check = () => {
			if (!this._captchaPendingPayload) return;
			if (this.captcha.getToken(form)) {
				this._onCaptchaTokenResolved();
			} else {
				this._captchaWatchTimer = setTimeout(check, 200);
			}
		};
		this._captchaWatchTimer = setTimeout(check, 200);
	}

	/**
	 * Clears the captcha token polling timer.
	 */
	_stopCaptchaTokenWatch() {
		if (this._captchaWatchTimer != null) {
			clearTimeout(this._captchaWatchTimer);
			this._captchaWatchTimer = null;
		}
	}

	/**
	 * Clears the challenge visibility polling timer.
	 */
	_stopChallengeVisibilityWatch() {
		if (this._challengeVisibilityTimer != null) {
			clearTimeout(this._challengeVisibilityTimer);
			this._challengeVisibilityTimer = null;
		}
	}

	/**
	 * Captures a snapshot of all relevant (non-captcha, non-hidden) form field values.
	 * Always includes `email` and `password` shortcut keys for backward compatibility.
	 * @param {HTMLFormElement} form
	 * @returns {Object}
	 */
	_snapshotFormState(form) {
		return snapshotFormFields(form);
	}

	/**
	 * Attempts to move focus to the injected hCaptcha challenge popup iframe.
	 * Tries multiple selectors; skips the invisible bound widget iframe.
	 * @returns {boolean} true if a challenge element was found and focused
	 */
	_focusCaptchaChallengeUi() {
		let selectors = [
			'iframe[src*="hcaptcha.com/captcha"]',
			'iframe[title*="hCaptcha challenge"]',
			'iframe[title*="Main content of the hCaptcha"]',
			'iframe[src*="hcaptcha.com"]:not([data-hcaptcha-widget-id])'
		];
		let challengeEl = null;
		for (let s of selectors) {
			try {
				let el = document.querySelector(s);
				if (el instanceof HTMLElement) {
					challengeEl = el;
					break;
				}
			} catch (_) {}
		}
		if (!challengeEl) return false;
		if (!challengeEl.hasAttribute('tabindex')) challengeEl.setAttribute('tabindex', '-1');
		requestAnimationFrame(() => this.focusEl(challengeEl));
		return true;
	}

	/**
	 * Saves full form state, closes quick-login, and focuses the hCaptcha challenge UI.
	 * Starts the captcha token watch so the modal can reopen once the challenge is solved.
	 * @param {HTMLFormElement} form
	 * @param {Element|null} wrapper
	 * @param {Element} root
	 */
	_escalateToChallengeUi(form, wrapper, root) {
		let active = document.activeElement;
		let focusField =
			active instanceof HTMLInputElement && form.contains(active) ? active.name || active.type || '' : '';

		this._captchaPendingPayload = {
			wrapper,
			root,
			focusField,
			formState: this._snapshotFormState(form)
		};

		// Unbind the close listener before programmatically closing so the native
		// `close` event does not trigger cancelInFlight and kill the captcha token watch.
		this._bindDialogClose(null);

		let dialog = new DialogController(wrapper);
		dialog.unlock();
		dialog.close();
		dialog.forceReleaseScrollLock();

		this._focusCaptchaChallengeUi();
		this._startCaptchaTokenWatch(form);
		this.logDebug('Quick-login closed; focused challenge UI; awaiting solve', {
			attemptId: this.loginAttemptId
		});
	}

	/**
	 * Attempts to manually trigger the hCaptcha visual challenge via hcaptcha.execute().
	 * @param {HTMLFormElement} form
	 * @returns {boolean} true if execute was called
	 */
	_tryTriggerChallenge(form) {
		try {
			let widgetId = this.captcha.getWidgetId(form);
			if (widgetId && typeof window !== 'undefined' && window.hcaptcha?.execute) {
				window.hcaptcha.execute(widgetId);
				return true;
			}
			return false;
		} catch (_) {
			return false;
		}
	}

	/**
	 * Polls briefly for hCaptcha challenge injection after a manual trigger attempt.
	 * On detection escalates to challenge UI focus path; on timeout falls back to redirect.
	 * @param {HTMLFormElement} form
	 * @param {Element|null} wrapper
	 * @param {Element} root
	 * @param {string} finalUrl
	 */
	_waitForChallengeVisibilityThenEscalate(form, wrapper, root, finalUrl) {
		this._stopChallengeVisibilityWatch();
		let maxMs = 3000;
		let intervalMs = 200;
		let elapsed = 0;
		let capturedAttemptId = this.loginAttemptId;

		let check = () => {
			if (capturedAttemptId !== this.loginAttemptId) return;

			if (this.captcha.isChallengeVisible()) {
				this.logDebug('Challenge detected after manual trigger; escalating to challenge UI', {
					attemptId: capturedAttemptId
				});
				this._escalateToChallengeUi(form, wrapper, root);
				return;
			}

			elapsed += intervalMs;
			if (elapsed >= maxMs) {
				this.logDebug('Challenge not detected after manual trigger; redirect fallback executed', {
					attemptId: capturedAttemptId,
					finalUrl: finalUrl || ''
				});
				this.setMessage(root, MESSAGES.SECURITY_REDIRECT, 'info');
				this.handoffToChallenge(form, wrapper, finalUrl, 'captcha-challenge-not-triggered');
				return;
			}

			this._challengeVisibilityTimer = setTimeout(check, intervalMs);
		};

		this._challengeVisibilityTimer = setTimeout(check, intervalMs);
	}

	/**
	 * Called when the captcha token is detected. Reopens the modal with saved form state.
	 */
	_onCaptchaTokenResolved() {
		this._stopCaptchaTokenWatch();
		let payload = this._captchaPendingPayload;
		this._captchaPendingPayload = null;
		if (!payload) return;

		let { wrapper, root } = payload;
		let dialog = new DialogController(wrapper);
		dialog.forceReleaseScrollLock();
		dialog.open();
		dialog.unlock();
		this.restoreFormState(wrapper, payload);
		this.setBusy(root, false);
		this.setSubmitLoading(root, false);
		this.setMessage(root, MESSAGES.SECURITY_RETRY_AFTER_CHALLENGE, 'info');
		this.focusLoginField(wrapper, payload.focusField || '');
		this.a11y.focusMessage();
		this.logDebug('Captcha solved; reopened quick login');
	}

	/**
	 * Gracefully hands off the flow to a full-page challenge or login.
	 * Stores reopen state and triggers navigation/submission after cleanup.
	 *
	 * @param {HTMLFormElement} form
	 * @param {Element|null} wrapper
	 * @param {string} [destinationUrl]
	 * @param {string} [reason]
	 */
	handoffToChallenge(form, wrapper, destinationUrl = '', reason = '') {
		this.cancelInFlight('handoff-to-challenge');
		SessionMemory.rememberReopen(wrapper, form, this.loginAttemptId, this.currentFlowId);
		SessionMemory.recordHandoff(form, reason, destinationUrl, this.currentFlowId);
		this.logDebug('Handing off to challenge', {
			attemptId: this.loginAttemptId,
			reason,
			destinationUrl: destinationUrl || form?.action || ''
		});

		let doNavigation = () => {
			if (destinationUrl) {
				try {
					window.location.href = destinationUrl;
					return;
				} catch (_) {}
			}

			try {
				form.requestSubmit ? form.requestSubmit() : form.submit();
			} catch (_) {}
		};

		setTimeout(doNavigation, 6000);
	}

	/**
	 * Shared logic for click and pointerdown on the submit button.
	 * Cancels on close-button, locks dialog for quick-login form, stops propagation.
	 * @param {Event} e
	 * @returns {boolean} false if the caller should return early (no submit action taken)
	 */
	_handleSubmitButtonEvent(e) {
		let target = e.target;
		if (!(target instanceof Element)) return false;

		if (target.closest('.cusa-dialog-close-btn')) {
			this.cancelInFlight('user-closed-dialog');
			return false;
		}

		let btn = target.closest('[data-cusa-login-submit]');
		if (!btn) return false;
		let form = btn.closest('form');
		if (!form?.classList.contains('cusa-quick-login-form')) return false;

		let wrapper = btn.closest('cusa-dialog');
		if (wrapper) {
			let dialog = new DialogController(wrapper);
			dialog.lock();
		}

		e.stopPropagation();
		e.stopImmediatePropagation?.();
		return true;
	}

	/**
	 * Click handler for submit button; locks dialog and prevents propagation.
	 * Cancels any in-flight request if dialog close button clicked.
	 * @param {MouseEvent} e
	 */
	onLoginButtonClick(e) {
		this._handleSubmitButtonEvent(e);
	}

	/**
	 * Pointerdown handler that mirrors the click handler behavior.
	 * Helps pre-lock the dialog when the user begins pressing the button.
	 * @param {PointerEvent} e
	 */
	onLoginButtonPointerDown(e) {
		this._handleSubmitButtonEvent(e);
	}

	/**
	 * Handler for native dialog `close` event; cancels active submission.
	 * @param {Event} _e
	 */
	onDialogClose(_e) {
		this.cancelInFlight('dialog-closed');
	}

	/**
	 * Resets dialog and UI state after an error or cancellation.
	 * @param {Element} root
	 * @param {DialogController} dialog
	 */
	resetDialogState(root, dialog) {
		dialog.unlock();
		this.setBusy(root, false);
		this.setSubmitLoading(root, false);
	}

	/**
	 * Handles common error pattern: set message, reset state, and optionally focus.
	 * @param {Element} root
	 * @param {DialogController} dialog
	 * @param {string} message
	 * @param {boolean} focusMessage
	 */
	handleError(root, dialog, message, focusMessage = false) {
		this.setMessage(root, message, 'error');
		this.resetDialogState(root, dialog);
		if (focusMessage) this.a11y.focusMessage();
	}

	/**
	 * Handles challenge detection and determines whether to redirect or show error.
	 *
	 * Attempts 1–3: shows inline SECURITY_TRY_AGAIN so the user can retry.
	 * At threshold: for captcha-token-missing, checks whether the hCaptcha visual challenge
	 * is already injected; if so, saves form state, closes quick-login, and focuses the
	 * challenge UI. If not, attempts a manual trigger and polls for injection. If challenge
	 * still does not appear, falls back to SECURITY_REDIRECT + handoff navigation.
	 * For all other reasons at threshold, falls back to SECURITY_REDIRECT + handoff.
	 *
	 * @param {Element} root
	 * @param {DialogController} dialog
	 * @param {HTMLFormElement} form
	 * @param {Element} wrapper
	 * @param {string} finalUrl
	 * @param {string} reason
	 * @returns {boolean} true if handoff was initiated
	 */
	handleChallenge(root, dialog, form, wrapper, finalUrl, reason) {
		if (this.captcha.shouldHandoff(form)) {
			this.logDebug('Escalation threshold reached', {
				attemptId: this.loginAttemptId,
				reason,
				finalUrl: finalUrl || ''
			});

			if (reason === 'captcha-token-missing') {
				if (this.captcha.isChallengeVisible()) {
					this.logDebug('Challenge detected pre-trigger; escalating to challenge UI', {
						attemptId: this.loginAttemptId
					});
					this._escalateToChallengeUi(form, wrapper, root);
				} else {
					this.logDebug('Manual trigger attempted; awaiting challenge injection', {
						attemptId: this.loginAttemptId
					});
					let triggered = this._tryTriggerChallenge(form);
					if (triggered) {
						this._waitForChallengeVisibilityThenEscalate(form, wrapper, root, finalUrl);
					} else {
						this.logDebug('Manual trigger failed; redirect fallback executed', {
							attemptId: this.loginAttemptId
						});
						this.setMessage(root, MESSAGES.SECURITY_REDIRECT, 'info');
						this.handoffToChallenge(form, wrapper, finalUrl, reason);
					}
				}
			} else {
				this.setMessage(root, MESSAGES.SECURITY_REDIRECT, 'info');
				this.handoffToChallenge(form, wrapper, finalUrl, reason);
			}
			return true;
		} else {
			this.logDebug('Challenge handled inline; user can retry', {
				attemptId: this.loginAttemptId,
				reason
			});
			this.handleError(root, dialog, MESSAGES.SECURITY_TRY_AGAIN);
			return false;
		}
	}

	/**
	 * Main form submission handler. Performs validation, captcha checks, and
	 * AJAX login with challenge detection and UI feedback.
	 * @param {SubmitEvent} e
	 */
	async onSubmit(e) {
		let form = e.target;
		if (!(form instanceof HTMLFormElement) || !form.classList.contains('cusa-quick-login-form')) return;

		let root = this.getLoginRoot(form);
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation?.();

		this.cancelInFlight('new-submit');
		this.clearMessage(root);
		this.a11y.clearFieldState(form);
		this.setBusy(root, true);
		this.setSubmitLoading(root, true);

		let wrapper = root.closest('cusa-dialog') || root.closest('[data-cusa-modal]');
		let dialog = new DialogController(wrapper);

		// Disable AJAX in collapsible mode
		if (wrapper?.hasAttribute('mode') && wrapper.getAttribute('mode') === 'collapsible') {
			form.requestSubmit ? form.requestSubmit() : form.submit();
			return;
		}

		dialog.lock();

		this.activeElement = wrapper;
		this.activeRoot = root;
		this.activeForm = form;

		let emailVal = this.getEmailField(form)?.value?.trim() || '';
		let passwordVal = this.getPasswordField(form)?.value || '';

		if (!emailVal || !passwordVal) {
			this.setMessage(root, MESSAGES.MISSING_FIELDS, 'error');
			this.a11y.setFieldInvalid(form, { email: !emailVal, password: !passwordVal });
			dialog.unlock();
			this.setBusy(root, false);
			this.setSubmitLoading(root, false);
			let field = !emailVal ? this.getEmailField(form) : this.getPasswordField(form);
			this.focusEl(field);
			return;
		}

		let attemptId = this.beginAttempt();
		this.logDebug('Started submit attempt', { attemptId });
		this.loginAbortController = new AbortController();
		let signal = this.loginAbortController.signal;

		this._bindDialogClose(wrapper?.querySelector('dialog') ?? null);

		// Captcha check
		if (this.captcha.isProtected(form)) {
			this.setMessage(root, MESSAGES.VERIFYING, 'info');
			let hasToken = await this.captcha.ensureToken(form, 12000, signal);
			if (signal.aborted || attemptId !== this.loginAttemptId) {
				this.clearMessage(root);
				return;
			}
			if (!hasToken) {
				this.clearMessage(root); // Clear verifying message before showing error
				this.handleChallenge(root, dialog, form, wrapper, '', 'captcha-token-missing');
				return;
			}
			// Token obtained successfully - keep verifying message until login completes
		}

		try {
			if (!navigator.onLine) {
				this.handleError(root, dialog, MESSAGES.NO_INTERNET);
				return;
			}

			let res = await fetch(form.action, {
				method: form.method || 'POST',
				body: new FormData(form),
				credentials: 'same-origin',
				redirect: 'follow',
				signal,
				headers: { 'X-Requested-With': 'XMLHttpRequest' }
			});

			let finalUrl = res?.url || '';
			let text = await res.text();

			if (signal.aborted || attemptId !== this.loginAttemptId) return;

			// Challenge detection
			if (finalUrl.includes('/challenge') || text.includes('shopify-challenge')) {
				this.handleChallenge(root, dialog, form, wrapper, finalUrl, 'shopify-challenge-detected');
				return;
			}

			if (!res.ok) {
				if (res.status === 429) {
					this.captcha.setCooldown(30000);
				}

				let textLower = text.toLowerCase();
				let looksLikeCaptcha =
					textLower.includes('captcha') || textLower.includes('hcaptcha') || finalUrl.includes('/challenge');

				let isForcedLoginPage = finalUrl.includes('/account/login');

				if (looksLikeCaptcha && isForcedLoginPage) {
					this.setMessage(root, MESSAGES.SECURITY_REDIRECT, 'info');
					this.handoffToChallenge(form, wrapper, finalUrl, 'forced-login-page');
					return;
				}

				if (looksLikeCaptcha) {
					this.handleChallenge(root, dialog, form, wrapper, finalUrl, 'captcha-like-response');
				} else {
					let msg = `Sign in failed (HTTP ${res.status}). Please try again.`;
					this.handleError(root, dialog, msg, true);
				}

				if (signal.aborted || attemptId !== this.loginAttemptId) return;
				return;
			}

			// Redirect back to login = invalid credentials
			if (finalUrl.includes('/account/login')) {
				this.setMessage(root, MESSAGES.BAD_CREDENTIALS, 'error');
				this.a11y.setFieldInvalid(form, { email: true, password: true });
				this.resetDialogState(root, dialog);
				this.focusEl(this.getPasswordField(form) || this.getEmailField(form));
				return;
			}

			// Success
			this.logDebug('Login succeeded; reloading page', {
				attemptId,
				finalUrl
			});
			// clear any stored session state now that login is complete
			try {
				// purge our canonical key and legacy keys
				sessionStorage.removeItem(SessionMemory.STATE_KEY);
				SessionMemory.purgeLegacyKeys();
			} catch (_) {
				// ignore if storage is unavailable
			}
			this.setSubmitLoading(root, true);
			this.setMessage(root, MESSAGES.SIGNING_IN, 'success');
			setTimeout(() => window.location.reload(), 500);
		} catch (err) {
			if (err?.name === 'AbortError') {
				this.resetDialogState(root, dialog);
				return;
			}

			this.handleError(root, dialog, MESSAGES.GENERIC_FAILURE, true);
		}
	}
}

customElements.define('cusa-quick-login', CusaQuickLogin);
